import { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { components } from 'react-select';
import { useAuth0 } from '@auth0/auth0-react';
import Axios from 'axios';
import { store, net_api_url } from 'components/Store';
import useGetAccessToken from 'components/Dashboard/apiCalls/useGetAccessToken';
import { CrmContactImage } from 'components/Dashboard/Influence/CrmContacts/CrmContactsTable/CrmContactItem/CrmContactItem';
import thousandCommas from 'components/Dashboard/helpers/thousandCommas';
import debounce from 'components/Dashboard/helpers/debounce';
import normalize from 'components/Dashboard/utilities/normalizeString';
import { useGroupFunctions } from 'components/Dashboard/Influence/CrmGroups/GroupFunctions';
import ContactsSelect from './ContactsSelectShared';

const MultiValueLabel = (props) => {
  const { imageId, imageUrl, label, stakeholderContactId, party } = props.data;
  const textRef = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useLayoutEffect(() => {
    if (textRef.current) {
      setIsTruncated(textRef.current.scrollWidth > textRef.current.clientWidth);
    }
  });

  return (
    <div
      className='d-flex align-items-center overflow-hidden gap-1 specific-y-padding-1 cursor-default'
      style={{ flex: 1, minWidth: 0 }}
      {...(isTruncated && { 'data-tooltip-id': 'general-tooltip', 'data-tooltip-content': label })}
    >
      <div className='d-flex flex-shrink-0 specific-padding-2'>
        <CrmContactImage
          imageId={imageId}
          imageUrl={imageUrl}
          size={25}
          className={
            stakeholderContactId
              ? `interaction-stakeholder-avatar-ring ${
                  party ? `member-${party.toLowerCase()}-picture` : 'interaction-stakeholder-avatar-ring-default'
                }`
              : ''
          }
        />
      </div>
      <components.MultiValueLabel {...props} innerProps={{ ...props.innerProps, ref: textRef }} />
      {stakeholderContactId && (
        <button
          type='button'
          className='general-button flex-shrink-0 open-stakeholder-page-btn px-1 h-100'
          data-tooltip-content='Open stakeholder page'
          data-tooltip-id='general-tooltip'
          style={{ width: '31px' }}
          onClick={(e) => {
            e.stopPropagation();
            window.open(`/stakeholders/${stakeholderContactId}/${normalize(label)}`, '_blank');
          }}
        >
          <span className='icon-external-link paragraph-p3-bold text-light-blue' />
        </button>
      )}
    </div>
  );
};

const PeopleAsyncSelect = ({
  onChange,
  value,
  placeholder = 'Search by group, tag or name',
  disabled = false,
  loadOptions: externalLoadOptions,
  defaultOptions = false,
  menuIsOpen,
  onMenuOpen,
  onMenuClose,
}) => {
  const { isAuthenticated } = useAuth0();
  const { getAccessToken } = useGetAccessToken();
  const { state } = useContext(store);
  const { net_api_url: apiUrl } = state;
  const { previewGroup } = useGroupFunctions();

  const selectRef = useRef(null);
  const axiosCancelToken = useRef(null);

  const [tagContacts, setTagContacts] = useState({});
  const [groupContacts, setGroupContacts] = useState({});
  const [inputValue, setInputValue] = useState('');

  // ponytail: stable ref so CrmOption deps stay empty and react-select never remounts options
  const optionStateRef = useRef({});

  const getAsyncOptions = useCallback(
    async (inputValue, tagContacts, groupContacts) => {
      if (!selectRef?.current?.inputRef?.value) return;
      try {
        const token = isAuthenticated ? await getAccessToken() : '';
        const baseUrl = apiUrl ?? net_api_url;
        const requestOptions = {
          signal: axiosCancelToken.current?.signal,
          ...(isAuthenticated ? { headers: { Authorization: `Bearer ${token}` } } : {}),
        };

        if (axiosCancelToken.current) axiosCancelToken.current.abort();
        axiosCancelToken.current = new AbortController();
        Object.assign(requestOptions, { signal: axiosCancelToken.current.signal });

        let contactTagsRes = {};
        let contactGroupsRes = {};

        const callContactResults = await Axios.get(
          `${baseUrl}/api/autocomplete/crm-contact?term=${inputValue}`,
          requestOptions
        );

        const getContactsTagsQntty = async (tag) => {
          const res = await Axios.post(
            `${baseUrl}/api/crm-contact/query`,
            { filters: [{ field: 'tags', value: tag, operator: 'str_eq' }], query: '', pageSize: 10000, pageNumber: 1 },
            requestOptions
          );
          contactTagsRes[tag] = (res?.data?.hits ?? []).map((c) => ({
            value: c.id,
            imageId: c.imageId,
            imageUrl: c.imageUrl,
            label: c.title,
            id: c.id,
            email: c.email,
            stakeholderContactId: c.contactId ?? null,
            party: c.party ?? null,
          }));
          return res.data.totalHits;
        };

        const getContactsGroupsQntty = async (value) => {
          const res = await previewGroup({ source: axiosCancelToken.current, id: value, pageSize: 10000 });
          contactGroupsRes[value] = (res?.hits ?? []).map((c) => ({
            value: c.id,
            imageId: c.imageId,
            label: c.title,
            id: c.id,
            email: c.email,
            stakeholderContactId: c.contactId ?? null,
            party: c.party ?? null,
          }));
          return res?.totalHits ?? 0;
        };

        const [callTagsResults, callGroupsResults] = await Promise.all([
          Axios.get(`${baseUrl}/api/autocomplete/crm-contact-tag?term=${inputValue}`, requestOptions),
          Axios.get(`${baseUrl}/api/autocomplete/crm-contact-group?term=${inputValue}`, requestOptions),
        ]);

        const tagResults = await Promise.all(
          callTagsResults.data.map(async (tag) => ({
            value: tag.value,
            label: tag.label,
            tagOption: true,
            contacts: tagContacts[tag.value]?.length ?? (await getContactsTagsQntty(tag.value)),
          }))
        );

        const groupResults = await Promise.all(
          (callGroupsResults.data ?? [])
            .filter((item) => item?.label?.toLowerCase().includes(inputValue?.toLowerCase().trim()))
            .map(async (item) => ({
              value: item.value,
              label: item.label,
              isGroup: true,
              contacts: groupContacts[item.value]?.length ?? (await getContactsGroupsQntty(item.value)),
            }))
        );

        const contactResults = callContactResults.data.map((c) => ({
          value: c.crmContactId,
          imageId: c.imageId,
          imageUrl: c.imageUrl,
          label: c.fullName,
          id: c.crmContactId,
          email: c.emailAddress,
          stakeholderContactId: c.stakeholderContactId ?? null,
          party: c.party ?? null,
        }));

        if (Object.keys(contactTagsRes).length > 0) setTagContacts((prev) => ({ ...prev, ...contactTagsRes }));
        if (Object.keys(contactGroupsRes).length > 0) setGroupContacts((prev) => ({ ...prev, ...contactGroupsRes }));

        return [
          {
            label: 'tags',
            options: tagResults.filter((t) => t.contacts !== 0).sort((a, b) => a.label.localeCompare(b.label)),
          },
          {
            label: 'groups',
            options: groupResults.filter((g) => g.contacts !== 0).sort((a, b) => a.label.localeCompare(b.label)),
          },
          {
            label: 'contacts',
            options: contactResults.length
              ? contactResults.sort((a, b) => a.label.localeCompare(b.label))
              : [{ value: 'No contacts', disabled: true, isDisabled: true }],
          },
        ];
      } catch {
        return [];
      }
    },
    [selectRef?.current?.inputRef?.value]
  );

  const loadOptions = useCallback(
    debounce((inputText, callback) => {
      if (inputText.length > 1) {
        getAsyncOptions(inputText, tagContacts, groupContacts).then((options) => callback(options));
      }
    }, 500),
    [tagContacts, groupContacts]
  );

  const onInputChange = (query, { action }) => {
    if (action !== 'set-value') setInputValue(query.replace(/\\/g, ''));
    else return inputValue;
  };

  const handleTagGroupSelect = useCallback(
    (data) => {
      const { tagContacts, groupContacts, value, onChange } = optionStateRef.current;
      const contacts = (data.tagOption ? tagContacts : groupContacts)[data.value] ?? [];
      const currentValue = value ?? [];
      const tagIds = new Set(contacts.map((c) => c.id));
      const allSelected = contacts.length > 0 && contacts.every((c) => currentValue.some((v) => v.id === c.id));
      if (allSelected) {
        onChange(currentValue.filter((v) => !tagIds.has(v.id)));
      } else {
        const currentIds = new Set(currentValue.map((v) => v.id));
        onChange([...currentValue, ...contacts.filter((c) => !currentIds.has(c.id))]);
      }
    },
    [] // ponytail: reads live state via ref, no deps needed
  );

  optionStateRef.current = { tagContacts, groupContacts, value, onChange, handleTagGroupSelect };

  const CrmOption = useCallback(
    ({ data, isSelected, isFocused, innerProps, innerRef }) => {
      const { tagOption, isGroup, disabled: optDisabled } = data;

      // The menu list's real scroll container is our own CustomScrollbar (see ContactsMenuList in
      // ContactsSelectShared.jsx), not react-select's own MenuList node - so react-select's built-in
      // keyboard scroll-into-view never touches it. Do it ourselves via the standard DOM API instead,
      // which scrolls whatever scrollable ancestor there is regardless of who owns it.
      const localRef = useRef(null);
      useEffect(() => {
        if (isFocused) localRef.current?.scrollIntoView?.({ block: 'nearest' });
      }, [isFocused]);
      const setRefs = (node) => {
        localRef.current = node;
        if (typeof innerRef === 'function') innerRef(node);
      };

      if (optDisabled) {
        return (
          <div style={{ padding: '6px 12px', color: 'rgba(0,18,43,0.5)', fontSize: 14 }}>No matching contacts</div>
        );
      }

      if (tagOption || isGroup) {
        const { tagContacts, groupContacts, value, handleTagGroupSelect } = optionStateRef.current;
        const contacts = (tagOption ? tagContacts : groupContacts)[data.value] ?? [];
        const allSelected = contacts.length > 0 && contacts.every((c) => (value ?? []).some((v) => v.id === c.id));
        return (
          <div
            ref={setRefs}
            className={`contacts-select-local-option w-100 d-flex align-items-center pr-3 ${
              isFocused ? 'contacts-select-local-option-focused' : ''
            }`}
            style={{ gap: 8 }}
            onClick={() => handleTagGroupSelect(data)}
          >
            <span className={isGroup ? 'icon-team d-inline-block' : 'icon-tag title-h3 d-inline-block'} />
            <span className='flex-grow-1'>
              {data.label} <span className='option-group-count'>({thousandCommas(data.contacts)})</span>
            </span>
            {allSelected && <span className='icon-simple-check text-light-blue' />}
          </div>
        );
      }

      return (
        <div
          ref={setRefs}
          {...innerProps}
          className={`contacts-select-local-option d-flex align-items-center pr-3 ${
            isFocused ? 'contacts-select-local-option-focused' : ''
          }`}
          style={{ gap: 8 }}
        >
          <CrmContactImage imageId={data.imageId} imageUrl={data.imageUrl} size={20} />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', flexGrow: 1 }}>{data.label}</span>
          {isSelected && <span className='icon-simple-check text-light-blue' />}
        </div>
      );
    },
    [] // ponytail: stable ref, react-select never remounts options on value change
  );

  return (
    <ContactsSelect
      selectRef={selectRef}
      defaultOptions={defaultOptions}
      loadOptions={externalLoadOptions ?? loadOptions}
      value={value}
      onChange={(selected) => {
        const list = selected ?? [];
        // Tag/group rows aren't real selectable values - clicking them expands into their
        // contacts instead (see handleTagGroupSelect). React-select's own keyboard selection
        // (Enter) doesn't know that and would otherwise append the raw tag/group row as-is.
        const specialOption = list.find((item) => item?.tagOption || item?.isGroup);
        if (specialOption) {
          handleTagGroupSelect(specialOption);
          return;
        }
        onChange(list);
      }}
      placeholder={placeholder}
      disabled={disabled}
      menuIsOpen={menuIsOpen}
      onMenuOpen={onMenuOpen}
      onMenuClose={onMenuClose}
      {...(externalLoadOptions == null && { inputValue, onInputChange })}
      MultiValueLabel={MultiValueLabel}
      Option={CrmOption}
      noOptionsMessage={
        externalLoadOptions == null ? ({ inputValue: iv }) => (iv?.length > 1 ? 'No results' : null) : undefined
      }
    />
  );
};

export default PeopleAsyncSelect;
